/**
 * Unsettled list omits both cancelled and delivered. A drop from that list is
 * ambiguous — resolve via order detail stage instead of assuming cancel.
 */

export type DroppedOrderResolution = {
  id: string;
  stage: string;
  updatedAt?: string | null;
};

export function selectDroppedUnsettledOrders<
  T extends { id: string; stage: string; createdAt?: string | null },
>(args: {
  prev: T[];
  activeIds: Set<string>;
  previouslySeenIds: Set<string>;
  createdAtFloorMs: number;
}): T[] {
  const { prev, activeIds, previouslySeenIds, createdAtFloorMs } = args;
  return prev.filter(
    (order) =>
      order.stage !== "cancelled" &&
      !activeIds.has(order.id) &&
      previouslySeenIds.has(order.id) &&
      (!order.createdAt || Date.parse(order.createdAt) >= createdAtFloorMs),
  );
}

/**
 * Apply detail-stage resolutions for orders that left the unsettled list.
 * - cancelled → keep card, mark cancelled
 * - delivered / other settled → remove from queue
 * - still unsettled → keep / update stage (rare race)
 */
export function applyDroppedUnsettledResolutions<
  T extends { id: string; stage: string; updatedAt?: string | null },
>(args: {
  prev: T[];
  resolutions: DroppedOrderResolution[];
  unsettledStages: readonly string[];
  cancelledAt: string;
}): { next: T[]; newlyCancelledCount: number } {
  const { prev, resolutions, unsettledStages, cancelledAt } = args;
  if (resolutions.length === 0) {
    return { next: prev, newlyCancelledCount: 0 };
  }

  const byId = new Map(resolutions.map((r) => [r.id, r]));
  let newlyCancelledCount = 0;
  const next: T[] = [];

  for (const order of prev) {
    const resolved = byId.get(order.id);
    if (!resolved) {
      next.push(order);
      continue;
    }

    if (resolved.stage === "cancelled") {
      if (order.stage !== "cancelled") newlyCancelledCount += 1;
      next.push({
        ...order,
        stage: "cancelled",
        updatedAt: resolved.updatedAt ?? cancelledAt,
      });
      continue;
    }

    if (unsettledStages.includes(resolved.stage)) {
      next.push({
        ...order,
        stage: resolved.stage,
        updatedAt: resolved.updatedAt ?? order.updatedAt,
      });
      continue;
    }

    // delivered (or any other settled stage) — drop from unsettled queue
  }

  return { next, newlyCancelledCount };
}
